import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  LayoutDashboard, Calculator, Users, FileText, Settings, LogOut, 
  Bell, Search, Menu, X, ChevronLeft, Receipt, Database, Shield,
  BarChart3, Clock, CheckCircle, AlertTriangle
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('autogutachter24_user');
    const token = localStorage.getItem('autogutachter24_token');
    
    if (!storedUser || !token) {
      navigate('/');
      return;
    }
    
    const userData = JSON.parse(storedUser);
    // Prüfe Admin-Rolle
    if (userData.rolle !== 'admin' && userData.rolle !== 'backoffice') {
      navigate('/dashboard');
      return;
    }
    
    setUser(userData);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('autogutachter24_user');
    localStorage.removeItem('autogutachter24_token');
    navigate('/');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/kalkulationen', icon: Calculator, label: 'Kalkulationen' },
    { path: '/admin/akten', icon: FileText, label: 'Alle Akten' },
    { path: '/admin/benutzer', icon: Users, label: 'Benutzer' },
    { path: '/admin/rechnungen', icon: Receipt, label: 'Rechnungen' },
    { path: '/admin/einstellungen', icon: Settings, label: 'Einstellungen' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 bottom-0 bg-gray-800 border-r border-gray-700 z-40 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
            <div className={`flex items-center space-x-3 ${!sidebarOpen && 'justify-center w-full'}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              {sidebarOpen && (
                <div>
                  <h1 className="text-lg font-bold text-white">Admin</h1>
                  <p className="text-xs text-gray-400">Backoffice</p>
                </div>
              )}
            </div>
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
            >
              {sidebarOpen ? <ChevronLeft className="w-5 h-5 text-gray-400" /> : <Menu className="w-5 h-5 text-gray-400" />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    active
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                  title={!sidebarOpen ? item.label : ''}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            })}
          </nav>

          {/* Zurück zum Hauptbereich */}
          <div className="p-3 border-t border-gray-700">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-gray-700 hover:text-white transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
              {sidebarOpen && <span className="text-sm">Zurück zur App</span>}
            </button>
          </div>

          {/* User Info */}
          <div className="p-3 border-t border-gray-700">
            <div className={`flex items-center ${sidebarOpen ? 'space-x-3' : 'justify-center'}`}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                {user.vorname?.[0]}{user.nachname?.[0]}
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user.vorname} {user.nachname}</p>
                  <p className="text-xs text-gray-400">{user.rolle}</p>
                </div>
              )}
            </div>
            {sidebarOpen && (
              <button
                onClick={handleLogout}
                className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-gray-400 hover:bg-red-600/20 hover:text-red-400 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Abmelden</span>
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 min-h-screen bg-gray-100 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Bar */}
        <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-20">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-white">
                  {menuItems.find(item => isActive(item.path, item.exact))?.label || 'Admin'}
                </h2>
              </div>
              <div className="flex items-center space-x-4">
                {/* Search */}
                <div className="flex items-center space-x-2 bg-gray-700 rounded-lg px-4 py-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Suchen..."
                    className="bg-transparent border-none outline-none text-sm text-white placeholder-gray-400 w-40"
                  />
                </div>
                {/* Notifications */}
                <button className="relative p-2 hover:bg-gray-700 rounded-lg transition-colors">
                  <Bell className="w-5 h-5 text-gray-400" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
